import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import MultiSelectBox from '../../Common/MultiSelectBox/MultiSelectBox';
import Button from '../../Common/Button';
import Toggle from '../../Common/Toggle/Toggle';
import { CsvToIntArray } from '../../../helpers/strings';
import { getMilestoneIds, getDateValue } from '../../../helpers/reports';
import '../Reports.scss';

export class ProjectDeploymentListFilters extends React.Component {

    constructor(props, context) {
        super(props, context);

        const initialFormSelections = {
            projectId: '',
            baselineIds: '',
            projectZeroDate: '',
            milestoneIds: '',
            continentIds: '',
            areaIds: '',
            regionIds: '',
            brandIds: '',
            managementCompanyIds: '',
            locationTypeIds: '',
            countryIds: '',
            isIncludeHiddenTasks: false,
            isIncludeBackupReports: false,
            dataMetricKey1: '',
            dataMetricKey2: ''
        }

        this.state = {
            showMoreFields: false,
            emptyForm: initialFormSelections,
            projectId: '',
            baselineIds: '',
            projectZeroDate: '',
            milestoneIds: '',
            continentIds: initialFormSelections.continentIds,
            areaIds: initialFormSelections.areaIds,
            regionIds: initialFormSelections.regionIds,
            brandIds: initialFormSelections.brandIds,
            managementCompanyIds: initialFormSelections.managementCompanyIds,
            locationTypeIds: initialFormSelections.locationTypeIds,
            countryIds: initialFormSelections.countryIds,
            isIncludeHiddenTasks: initialFormSelections.isIncludeBackupReports,
            isIncludeBackupReports: initialFormSelections.isIncludeHiddenTasks,
            dataMetricKey1: initialFormSelections.dataMetricKey1,
            dataMetricKey2: initialFormSelections.dataMetricKey2
        }
    }

    componentDidMount = async () => {
        /**
         * Must set defaults before registering report filter functions, because
         * these defaults might be overridden by mapToForm values coming from
         * the API when opening a scheduled report.
         */
        this.setDefaults();
        /**
         * On mount, register all of this form's callbacks so the parent is able
         * to use them when its wrapping UI indicates these actions should be
         * performed.
         */
        this.props.registerReportFilterFunctions(
            this.props.report.id,
            this.clearForm,
            this.validateForm,
            this.mapToForm,
            this.mapToApiRequest
        );
    }

    setDefaults = () => {
        let newState = { ...this.state };

        const appState = this.props.savedFilterValues;
        if (appState) {
            // Set new state to values from redux app state
            newState = {
                ...newState,
                locationKeys: appState.locationKeys ? appState.locationKeys : newState.locationKeys,
                projectId: appState.projectId ? appState.projectId : newState.projectId,
                baselineIds: appState.baselineIds ? appState.baselineIds : newState.baselineIds,
                projectZeroDate: appState.projectZeroDate ? appState.projectZeroDate : newState.projectZeroDate,
                milestoneIds: appState.milestoneIds ? appState.milestoneIds : newState.milestoneIds,
                continentIds: appState.continentIds ? appState.continentIds : newState.continentIds,
                areaIds: appState.areaIds ? appState.areaIds : newState.areaIds,
                regionIds: appState.regionIds ? appState.regionIds : newState.regionIds,
                brandIds: appState.brandIds ? appState.brandIds : newState.brandIds,
                managementCompanyIds: appState.managementCompanyIds ? appState.managementCompanyIds : newState.managementCompanyIds,
                locationTypeIds: appState.locationTypeIds ? appState.locationTypeIds : newState.locationTypeIds,
                countryIds: appState.countryIds ? appState.countryIds : newState.countryIds,
                communityIds: appState.communityIds ? appState.communityIds : newState.communityIds,
                isIncludeBackupReports: appState.isIncludeBackupReports ? appState.isIncludeBackupReports : newState.isIncludeBackupReports,
                isIncludeHiddenTasks: appState.isIncludeHiddenTasks ? appState.isIncludeHiddenTasks : newState.isIncludeHiddenTasks,
                dataMetricKey1: appState.dataMetricKey1 ? appState.dataMetricKey1 : newState.dataMetricKey1,
                dataMetricKey2: appState.dataMetricKey2 ? appState.dataMetricKey2 : newState.dataMetricKey2
            }
        }

        let projectId = this.getDefaultProjectId(newState.projectId);

        // Possibly override app state values with default values or URL params
        newState = {
            ...newState,
            projectId: projectId
        }

        this.setState({
            ...this.state,
            ...newState
        });

        /**
         * If newState.projectId has a value, then we need to trigger a project
         * change to make sure we immediately load the ProjectZeroDate and 
         * Project Baseline options, because this does not get automatically 
         * triggered just by setting the value in state.
         */
        if (newState.projectId) {
            this.props.onProjectChange(newState.projectId);
        }
    }

    getDefaultProjectId = (savedProjectId) => {
        /**
         * Project IDs(s) can be sent in via the URL params.  If they are set there
         * then we override the default or saved values and select the values from
         * the URL params
         */
        let projectId = '';
        const urlProjectId = this.props.urlParams.projectId;
        if (urlProjectId && urlProjectId.trim().length > 0) {
            projectId = urlProjectId;
        } else if (savedProjectId) {
            projectId = savedProjectId;
        }

        return projectId;
    }

    clearForm = () => {
        this.setState({
            ...this.state,
            ...this.state.emptyForm
        });
    }

    validateForm = () => {
        if (!this.state.projectId || this.state.projectId <= 0 || !this.state.dataMetricKey1 || this.state.dataMetricKey1.trim().length <= 0) {
            return false;
        }

        return true;
    }

    mapToForm = (reportRequest) => {
        const form = { ...this.state.emptyForm };

        form.locationKeys = (reportRequest.locationKeys) ? reportRequest.locationKeys.join(',') : form.locationKeys;
        form.projectId = (reportRequest.projectId) ? reportRequest.projectId : form.projectId;
        form.baselineIds = (reportRequest.baselineIds) ? reportRequest.baselineIds.join(',') : form.baselineIds;
        form.projectZeroDate = getDateValue(reportRequest.projectZeroDate);
        form.milestoneIds = getMilestoneIds(reportRequest.isMilestone);
        form.continentIds = (reportRequest.continentIds) ? reportRequest.continentIds.join(',') : form.continentIds;
        form.areaIds = (reportRequest.areaIds) ? reportRequest.areaIds.join(',') : form.areaIds;
        form.regionIds = (reportRequest.regionIds) ? reportRequest.regionIds.join(',') : form.regionIds;
        form.brandIds = (reportRequest.brandIds) ? reportRequest.brandIds.join(',') : form.brandIds;
        form.managementCompanyIds = (reportRequest.managementCompanyIds) ? reportRequest.managementCompanyIds.join(',') : form.managementCompanyIds;
        form.locationTypeIds = (reportRequest.locationTypeIds) ? reportRequest.locationTypeIds.join(',') : form.locationTypeIds;
        form.countryIds = (reportRequest.countryIds) ? reportRequest.countryIds.join(',') : form.countryIds;
        form.communityIds = (reportRequest.communityIds) ? reportRequest.communityIds.join(',') : form.communityIds;
        form.isIncludeHiddenTasks = (reportRequest.isIncludeHiddenTasks === true) ? true : false;
        form.isIncludeBackupReports = (reportRequest.isIncludeBackupReports === true) ? true : false;
        form.dataMetricKey1 = (reportRequest.dataMetricKey1) ? reportRequest.dataMetricKey1 : form.dataMetricKey1;
        form.dataMetricKey2 = (reportRequest.dataMetricKey2) ? reportRequest.dataMetricKey2 : form.dataMetricKey2;

        this.setState({
            ...this.state,
            ...form
        });

        /** 
         * Make sure to trigger the parent's loading of the proper project dependent
         * filter values
         */
        if (reportRequest.projectId) {
            this.props.onProjectChange(reportRequest.projectId);
        }
    }

    mapToApiRequest = () => {
        let isMilestone = null; // default to none or both
        if (this.state.milestoneIds.length > 0) {
            const selectedMilestoneIds = this.state.milestoneIds.split(',');
            if (selectedMilestoneIds.length == 1) {
                isMilestone = selectedMilestoneIds[0] === '1' ? true : false;
            }
        }

        const request = {
            reportId: this.props.report.id,
            projectId: this.state.projectId,
            projectZeroDate: this.state.projectZeroDate,
            baselineIds: CsvToIntArray(this.state.baselineIds),
            brandIds: CsvToIntArray(this.state.brandIds),
            regionIds: CsvToIntArray(this.state.regionIds),
            managementCompanyIds: CsvToIntArray(this.state.managementCompanyIds),
            continentIds: CsvToIntArray(this.state.continentIds),
            areaIds: CsvToIntArray(this.state.areaIds),
            isMilestone: isMilestone,
            locationTypeIds: CsvToIntArray(this.state.locationTypeIds),
            countryIds: CsvToIntArray(this.state.countryIds),
            isIncludeBackupReports: this.state.isIncludeBackupReports,
            isIncludeHiddenTasks: this.state.isIncludeHiddenTasks,
            dataMetricKey1: this.state.dataMetricKey1,
            dataMetricKey2: this.state.dataMetricKey2
        };

        return {
            apiRequest: request,
            formValues: this.state
        };
    }

    onToggleShowMore = () => {
        this.setState({
            showMoreFields: !this.state.showMoreFields
        });
    }

    onFieldChange = (name, value) => {
        this.setState({ ...this.state, [name]: value });
    }

    onDropdownChange = (value, name) => {
        this.onFieldChange(name, value);
    }

    onProjectChange = (value, name) => {
        // Save the changed value in our local state, as usual
        this.onFieldChange(name, value);

        // Tell our parent the project value has changed so they can reload the
        // options for the Project Zero Date and Baseline fields
        if (value) {
            this.props.onProjectChange(value);
        }
    }

    render() {
        const showMoreText = this.state.showMoreFields ? "Show Less Filters" : "Show More Filters";
        /**
         * Project Dependent Filters don't exist until a project has been selected,
         * so we must ensure we send MultiSelectBox an empty array instead of undefined
         * or it throws errors
         */
        const projectZeroDateOptions = this.props.filters.projectZeroDates ? this.props.filters.projectZeroDates : [];
        const projectBaselineOptions = this.props.filters.projectBaselines ? this.props.filters.projectBaselines : [];

        return (
            <form id="subForm" noValidate className={this.props.isFormValid ? "" : "was-validated"}>
                <div className="row">
                    <div className="col-sm-12 mb-3">
                        <strong>GROUP BY</strong>
                    </div>
                </div>
                <div className="row">
                    <div className="col-sm-12 col-md-4">
                        <MultiSelectBox name="dataMetricKey1"
                            label="Data Metric Choice 1" idField="id" valueField="name"
                            options={this.props.filters.groupings}
                            selectedValues={this.state.dataMetricKey1.toString()}
                            onChange={this.onDropdownChange}
                            isRequired={true}
                            errorMessage="Data Metric Choice 1 is Required"
                            isMulti={false} />
                    </div>
                    <div className="col-sm-12 col-md-4">
                        <MultiSelectBox name="dataMetricKey2"
                            label="Data Metric Choice 2" idField="id" valueField="name"
                            options={this.props.filters.groupings}
                            selectedValues={this.state.dataMetricKey2.toString()}
                            onChange={this.onDropdownChange}
                            isRequired={false}
                            isMulti={false} />
                    </div>
                    <div className="col-sm-12 col-md-4">

                    </div>
                </div>
                <div className="row">
                    <div className="col-sm-12 mb-3">
                        <strong>FILTERS</strong>
                    </div>
                </div>
                <div className="row">
                    <div className="col-sm-12 col-md-4">
                        <MultiSelectBox name="projectId"
                            label="Project" idField="id" valueField="name"
                            options={this.props.filters.projects}
                            selectedValues={this.state.projectId.toString()}
                            onChange={this.onProjectChange}
                            isRequired={true}
                            errorMessage="Project is required"
                            isMulti={false} />
                    </div>
                    <div className="col-sm-12 col-md-4">
                        <MultiSelectBox name="baselineIds"
                            label="Baseline" idField="id" valueField="name"
                            options={projectBaselineOptions}
                            selectedValues={this.state.baselineIds.toString()}
                            onChange={this.onDropdownChange}
                            isRequired={false}
                            isMulti={true} />
                    </div>
                    <div className="col-sm-12 col-md-4">
                        <MultiSelectBox name="projectZeroDate"
                            label="Project Zero Date / Anticipated Completion" idField="id" valueField="name"
                            options={projectZeroDateOptions}
                            selectedValues={this.state.projectZeroDate.toString()}
                            onChange={this.onDropdownChange}
                            isRequired={false}
                            isMulti={false} />
                    </div>
                </div>
                {this.state.showMoreFields &&
                    <Fragment>
                        <div className="row">
                            <div className="col-sm-12 col-md-4">
                                <MultiSelectBox name="milestoneIds"
                                    label="Milestone" idField="id" valueField="name"
                                    options={this.props.filters.milestones}
                                    selectedValues={this.state.milestoneIds.toString()}
                                    onChange={this.onDropdownChange}
                                    isRequired={false}
                                    isMulti={true} />
                            </div>
                            <div className="col-sm-12 col-md-4">
                                <MultiSelectBox name="continentIds"
                                    label="Continent" idField="id" valueField="name"
                                    options={this.props.filters.continents}
                                    selectedValues={this.state.continentIds.toString()}
                                    onChange={this.onDropdownChange}
                                    isRequired={false}
                                    isMulti={true} />
                            </div>
                            <div className="col-sm-12 col-md-4">
                                <MultiSelectBox name="locationTypeIds"
                                    label="Location Type" idField="id" valueField="name"
                                    options={this.props.filters.locationTypes}
                                    selectedValues={this.state.locationTypeIds.toString()}
                                    onChange={this.onDropdownChange}
                                    isRequired={false}
                                    isMulti={true} />
                            </div>
                        </div>

                        <div className="row">
                            <div className="col-sm-12 col-md-4">
                                <MultiSelectBox name="brandIds"
                                    label="Brand" idField="id" valueField="name"
                                    options={this.props.filters.brands}
                                    selectedValues={this.state.brandIds.toString()}
                                    onChange={this.onDropdownChange}
                                    isRequired={false}
                                    isMulti={true} />
                            </div>
                            <div className="col-sm-12 col-md-4">
                                <MultiSelectBox name="regionIds"
                                    label="Region" idField="id" valueField="name"
                                    options={this.props.filters.regions}
                                    selectedValues={this.state.regionIds.toString()}
                                    onChange={this.onDropdownChange}
                                    isRequired={false}
                                    isMulti={true} />
                            </div>
                            <div className="col-sm-12 col-md-4">
                                <MultiSelectBox name="areaIds"
                                    label="Area" idField="id" valueField="name"
                                    options={this.props.filters.areas}
                                    selectedValues={this.state.areaIds.toString()}
                                    onChange={this.onDropdownChange}
                                    isRequired={false}
                                    isMulti={true} />
                            </div>
                        </div>
                        <div className="row">
                            <div className="col-sm-12 col-md-4">
                                <MultiSelectBox name="countryIds"
                                    label="Country" idField="id" valueField="name"
                                    options={this.props.filters.countries}
                                    selectedValues={this.state.countryIds.toString()}
                                    onChange={this.onDropdownChange}
                                    isRequired={false}
                                    isMulti={true} />
                            </div>
                            <div className="col-sm-12 col-md-4">
                                <MultiSelectBox name="managementCompanyIds"
                                    label="Management Company" idField="id" valueField="name"
                                    options={this.props.filters.managementCompanies}
                                    selectedValues={this.state.managementCompanyIds.toString()}
                                    onChange={this.onDropdownChange}
                                    isRequired={false}
                                    isMulti={true} />
                            </div>
                            <div className="col-sm-12 col-md-4">
                                <Toggle name="isIncludeBackupReports"
                                    text="Include Backup Reports"
                                    checked={this.state.isIncludeBackupReports}
                                    onChange={this.onFieldChange} />
                                <Toggle name="isIncludeHiddenTasks"
                                    text="Include Hidden Tasks"
                                    checked={this.state.isIncludeHiddenTasks}
                                    onChange={this.onFieldChange} />
                            </div>
                        </div>
                    </Fragment>
                }
                <div className="row text-end">
                    <div className="col-sm-12">
                        <Button name="showMore" type="button" bootstrapClass="link" text={showMoreText} onClick={this.onToggleShowMore} />
                    </div>
                </div>
            </form>
        );
    }
}

ProjectDeploymentListFilters.propTypes = {
    report: PropTypes.object.isRequired,
    filters: PropTypes.object.isRequired,
    isFormValid: PropTypes.bool.isRequired,
    registerReportFilterFunctions: PropTypes.func.isRequired,
    urlParams: PropTypes.object.isRequired,
    savedFilterValues: PropTypes.object.isRequired,
    onProjectChange: PropTypes.func.isRequired
};

export default ProjectDeploymentListFilters;
